import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GalleryDto, GalleryPresetDto, StandardGalleryPresetDto, TenantDto } from '@nusign/picflow-backend-types';
import { filter, switchMap } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { getPresetChanges } from '../../../lib/new-gallery';
import { CheckoutService } from '../../../services/checkout.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryPresetService } from '../../../services/gallery-preset.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { SaveGalleryPresetDialogComponent } from '../../dialogs/save-gallery-preset-dialog/save-gallery-preset-dialog.component';
import { findPreset } from '../../lib/find-preset';

@Component({
  selector: 'app-gallery-settings-presets-dropdown',
  imports: [ContextMenuComponent, ButtonDirective, SvgComponent, ButtonMenuItemComponent, UpgradeBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gallery-settings-presets-dropdown.component.html',
  styleUrl: './gallery-settings-presets-dropdown.component.scss',
})
export class GallerySettingsPresetsDropdownComponent {
  private galleryPresetService = inject(GalleryPresetService);
  private galleryService = inject(GalleryService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(CheckoutService);
  private state = inject(StateService);
  private dialog = inject(DialogService);

  gallery = input.required<GalleryDto>();
  tenant = computed(() => this.gallery()?.tenant as TenantDto);
  isTeamMember = computed(() => {
    const tenant = this.tenant();
    if (!tenant) return;
    return this.state.tenants?.some(t => t.id === tenant.id);
  });

  customPresets = toSignal(
    toObservable(this.isTeamMember).pipe(
      filter(Boolean),
      switchMap(() => this.galleryPresetService.getCustomGalleryPresets())
    )
  );

  standardPresets = toSignal(
    toObservable(this.isTeamMember).pipe(
      filter(Boolean),
      switchMap(() => this.galleryPresetService.getStandardGalleryPresets())
    )
  );

  currentPreset = computed(() => {
    const gallery = this.gallery();
    const customPresets = this.customPresets();
    const standardPresets = this.standardPresets();
    if (!gallery || !customPresets || !standardPresets) return;
    return findPreset(gallery, standardPresets, customPresets);
  });

  applyPreset(preset: StandardGalleryPresetDto | GalleryPresetDto) {
    const params = getPresetChanges(preset);
    this.galleryService.updateGallery(this.gallery(), params);
  }

  saveCustomPreset() {
    if (!this.permissionService.canDisplayElement(billingScopes.gallery.customPresets)) {
      return this.checkoutService.open(billingScopes.gallery.customPresets);
    }
    this.dialog.open(SaveGalleryPresetDialogComponent, { gallery: this.gallery() });
  }
}
